import { FC, useCallback, useContext, useRef, useState } from 'react';

import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next/pages';

import { Button, Form, Input, Space, Switch } from 'antd';
import cn from 'classnames';
import debounce from 'lodash/debounce';

import { registerCompany } from '@/api/auth';
import { getInfoByInn } from '@/api/companies/get-info-by-inn';
import { useDocsList } from '@/api/docs';
import { customizeRequiredMark, PhoneInput } from '@/components/common';
import { FieldLabel } from '@/components/RegistrationForm/FieldLabel';
import { FnsField } from '@/components/RegistrationForm/Fns-Field';
import { FeedbackContext } from '@/context/feeadback.context';
import { TranslationNamespace } from '@/i18n';
import {
	ymOnFishCompanyRegistrationError,
	ymOnFishCompanyRegistrationInput,
	ymOnFishCompanyRegistrationSuccess,
} from '@/metrics';
import { CompanyRegistrationPayload, Credentials } from '@/types/api/auth';
import { CompanyStatus, InfoByInn } from '@/types/company/info-by-inn';
import { Layout } from '@/types/landing-forms';
import { getMappingDocs } from '@/utils/docs';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';

import { CenteredRelativeLoaderWithBlur } from '../CenteredRelativeLoaderWithBlur/CenteredRelativeLoaderWithBlur';
import { PhoneInputWithoutCode } from '../common/PhoneInputWithoutCode';

import styles from './styles.module.scss';

type RegistrationFormFields = CompanyRegistrationPayload & {
	agreement: boolean;
};

type Props = {
	withHeader?: boolean;
	registrationButtonText?: string;
	onSuccess: (data: Credentials) => Promise<void>;
};

const companyStatusKeys = {
	[CompanyStatus.ACTIVE]: 'registration-form.company-status.active',
	[CompanyStatus.LIQUIDATING]: 'registration-form.company-status.liquidating',
	[CompanyStatus.LIQUIDATED]: 'registration-form.company-status.liquidated',
	[CompanyStatus.REORGANIZING]: 'registration-form.company-status.reorganizing',
	[CompanyStatus.BANKRUPT]: 'registration-form.company-status.bankrupt',
};

export const RegistrationForm: FC<Props> = ({
	withHeader = true,
	registrationButtonText,
	onSuccess,
}) => {
	const { message } = useContext(FeedbackContext);
	const [isLoading, setIsLoading] = useState(false);
	const router = useRouter();

	const [isLoadingFnsData, setIsLoadingFnsData] = useState(false);

	const [dadataCompanyName, setDadataCompanyName] = useState<string>(null);
	const [fnsDataError, setFnsDataError] = useState('');
	const { data: docsList, isLoading: docsLoading } = useDocsList(getMappingDocs);

	const [agreed, setAgreed] = useState(false);
	const [formValid, setFormValid] = useState(false);

	const [form] = Form.useForm();
	const t = useTranslation(TranslationNamespace.LoginRegistration).t;

	const firstInputTrack = useRef(new Set<string>());

	const onChange = (fieldName: string) => {
		if (!firstInputTrack.current.has(fieldName)) {
			firstInputTrack.current.add(fieldName);
			ymOnFishCompanyRegistrationInput(fieldName);
		}
	};

	const validateForm = useCallback(
		debounce(async () => {
			try {
				await form.validateFields({ validateOnly: true, dirty: true });
				setFormValid(true);
			} catch (e) {
				setFormValid(false);
			}
		}, 300),
		[],
	);

	const handleValuesChanged = (field: Partial<RegistrationFormFields>) => {
		void validateForm();
		if (field.taxId !== undefined) {
			return handleTaxIdChange(field.taxId);
		}
		if (field.agreement !== undefined) {
			return setAgreed(field.agreement);
		}
	};

	const handleRegisterCompany = async () => {
		if (isLoading) return;
		setIsLoading(true);
		try {
			const data: CompanyRegistrationPayload = await form.validateFields();
			const preparedData = Object.entries(data).reduce((acc, [key, value]) => {
				if (value) {
					acc[key] = value;
				}
				return acc;
			}, {} as CompanyRegistrationPayload);
			await registerCompany(preparedData);
			ymOnFishCompanyRegistrationSuccess();
			message.success(t('registration-form.the-company-has-been-registered-successfully'));
			return onSuccess(data);
		} catch (e) {
			ymOnFishCompanyRegistrationError();
			message.error(e);
			setIsLoading(false);
		}
	};

	const handleTaxIdChange = debounce(async (value: string) => {
		setIsLoadingFnsData(true);
		try {
			await form.validateFields(['taxId']);
			const companyInfo: InfoByInn = await getInfoByInn(value);
			if (companyInfo) {
				if (companyInfo.status !== CompanyStatus.ACTIVE) {
					setFnsDataError(
						t('registration-form.company-registration-unavailable', {
							status: t(companyStatusKeys[companyInfo.status]),
						}),
					);
				} else {
					setDadataCompanyName(companyInfo.titleShort);
					setFnsDataError('');
				}
			} else {
				setFnsDataError(t('registration-form.no-matches-were-found-for-this-tin'));
			}
		} catch (e) {
			setDadataCompanyName(null);
			setFnsDataError('');
		} finally {
			setIsLoadingFnsData(false);
		}
	}, 400);

	const taxIdValidateStatus = () => {
		if (!form.isFieldTouched(['taxId'])) {
			return '';
		}
		const formError = form.getFieldError('taxId');
		if ((form.isFieldTouched(['taxId']) && fnsDataError?.length) || !!formError.length) {
			return 'error';
		}
		return 'success';
	};

	return (
		<div className={styles.container}>
			{docsLoading ? (
				<CenteredRelativeLoaderWithBlur isLoading={docsLoading} />
			) : (
				<>
					{withHeader && (
						<h1 className={styles.title}>
							{t('registration-form.company-administrator-registration')}
						</h1>
					)}
					<div className={styles.descriptionBlock}>
						<div>
							Вы регистрируете аккаунт вашей рыбной компании и становитесь её администратором в
							системе Fishplace.
						</div>
						<div>После входа в личный кабинет вы сможете:</div>
						<div>
							<div className={styles.descriptionPoint}>
								<CheckOutlined className={styles.icon} /> Заполнить профиль компании
							</div>
							<div className={styles.descriptionPoint}>
								<CheckOutlined className={styles.icon} />
								Пригласить сотрудников вашей компании
							</div>
							<div className={styles.descriptionPoint}>
								<CheckOutlined className={styles.icon} />
								Настроить права доступа
							</div>
						</div>
					</div>

					<Form
						form={form}
						className={styles.form}
						layout={Layout.Vertical}
						onFinish={handleRegisterCompany}
						onValuesChange={handleValuesChanged}
						requiredMark={customizeRequiredMark}
					>
						<div className={styles.field}>
							<Form.Item<RegistrationFormFields>
								name="taxId"
								label={<FieldLabel text={t('registration-form.tax-id')} />}
								hasFeedback
								validateFirst
								shouldUpdate
								validateStatus={taxIdValidateStatus()}
								rules={[
									{
										required: true,
										message: (
											<span>{t('registration-form.enter-taxpayer-identification-number')}</span>
										),
									},
									{
										pattern: /^[0-9]+$/,
										message: <span>{t('registration-form.tax-id-can-only-contain-numbers')}</span>,
									},
									{
										min: 10,
										max: 12,
										message: (
											<span>
												{t('registration-form.the-length-of-the-tin-is-from-10-to-12-characters')}
											</span>
										),
									},
								]}
							>
								<Input
									size="large"
									className={styles.input}
									placeholder={t('registration-form.enter-taxpayer-identification-number')}
									onChange={() => onChange('taxId')}
								/>
							</Form.Item>
							{!form.getFieldError(['taxId']).length && (
								<FnsField
									companyName={dadataCompanyName}
									error={fnsDataError}
									loading={isLoadingFnsData}
								/>
							)}
						</div>
						<Form.Item<RegistrationFormFields>
							name="email"
							label={<FieldLabel text={t('registration-form.email-login-to-the-system')} />}
							hasFeedback
							rules={[
								{
									required: true,
									type: 'email',
									message: <span>{t('registration-form.please-enter-your-email-address')}</span>,
								},
							]}
						>
							<Input
								size="large"
								className={styles.formItemCompact}
								placeholder={t('registration-form.email')}
								onChange={() => onChange('email')}
							/>
						</Form.Item>

						<div className={styles.nameWrapper}>
							<Form.Item<RegistrationFormFields>
								className={styles.name}
								name="lastName"
								label={<FieldLabel text={t('registration-form.last-name')} />}
							>
								<Input
									size="large"
									className={styles.formItemCompact}
									placeholder={t('registration-form.ivanov')}
								/>
							</Form.Item>
							<Form.Item<RegistrationFormFields>
								name="firstName"
								className={styles.name}
								label={<FieldLabel text={t('registration-form.first-name')} />}
							>
								<Input
									size="large"
									className={styles.formItemCompact}
									placeholder={t('registration-form.ivan')}
								/>
							</Form.Item>
						</div>

						<Form.Item<RegistrationFormFields>
							name="phone"
							label={<FieldLabel text={t('registration-form.phone-number')} />}
							validateFirst
							rules={[
								{
									required: true,
									message: <span>{t('registration-form.please-enter-your-phone-number')}</span>,
								},
								{
									pattern: /\d+/,
									message: (
										<span>{t('registration-form.phone-number-can-only-contain-numbers')}</span>
									),
								},
							]}
						>
							<PhoneInputWithoutCode
								sizeType="large"
								name="phone"
								onChange={() => onChange('phone')}
								disabledSelect
							/>
						</Form.Item>

						<Form.Item<RegistrationFormFields>
							name="password"
							label={
								<FieldLabel
									text={t(
										'registration-form.password-minimum-8-characters-capital-letter-and-number',
									)}
								/>
							}
							hasFeedback
							validateFirst
							rules={[
								{
									required: true,
									message: <span>{t('registration-form.enter-password')}</span>,
								},
								{
									min: 8,
									max: 32,
									message: (
										<span>{t('registration-form.password-must-contain-from-8-to-32-symbols')}</span>
									),
								},
								{
									pattern: /[a-z]/,
									message: (
										<span>
											{t(
												'registration-form.the-password-must-contain-at-least-one-lowercase-letter',
											)}
										</span>
									),
								},
								{
									pattern: /[A-Z]/,
									message: (
										<span>
											{t('registration-form.password-must-contain-at-least-one-capital-letter')}
										</span>
									),
								},
								{
									pattern: /[0-9]/,
									message: (
										<span>
											{t('registration-form.the-password-must-contain-at-least-one-number')}
										</span>
									),
								},
								{
									pattern: /[!@#$^&?_\\-]/,
									message: (
										<span>
											{t(
												'registration-form.the-password-must-contain-at-least-one-special-character',
											)}
										</span>
									),
								},
							]}
						>
							<Input.Password
								size="large"
								className={styles.formItemCompact}
								type="password"
								placeholder={t('registration-form.enter-your-password')}
								autoComplete="new-password"
								onChange={() => onChange('password')}
							/>
						</Form.Item>
						<Form.Item<RegistrationFormFields>
							name="passwordConfirmation"
							label={<FieldLabel text={t('registration-form.repeat-the-password')} />}
							hasFeedback
							validateFirst
							dependencies={['password']}
							rules={[
								{
									required: true,
									message: <span>{t('registration-form.enter-your-password')}</span>,
								},
								({ getFieldValue }) => ({
									validator(_, value) {
										if (!value || getFieldValue('password') === value) {
											return Promise.resolve();
										}
										return Promise.reject(new Error());
									},
									message: <span>{t('registration-form.passwords-do-not-match')}</span>,
								}),
							]}
						>
							<Input.Password
								size="large"
								className={styles.formItemCompact}
								type="password"
								placeholder={t('registration-form.repeat-the-password')}
								autoComplete="new-password"
								onChange={() => onChange('passwordConfirmation')}
							/>
						</Form.Item>
						<Space className={styles.switchBlock}>
							<Form.Item name="agreement" valuePropName="checked">
								<Switch
									checkedChildren={<CheckOutlined />}
									unCheckedChildren={<CloseOutlined />}
									onChange={() => onChange('agreement')}
								/>
							</Form.Item>
							<div
								className={cn(styles.switchBlock, {
									[styles.enSwitchBlock]: router.locale === 'en',
								})}
							>
								<div className={styles.switchDescription}>
									{t('registration-form.i-agree-with')}
									<a
										href={docsList?.privacyPolicy}
										className={styles.link}
										target="_blank"
										rel="noreferrer"
									>
										{t('registration-form.the-privacy-policy')}
									</a>
									&nbsp;{t('registration-form.and')}&nbsp;
									<a
										href={docsList?.userAgreement}
										className={styles.link}
										target="_blank"
										rel="noreferrer"
									>
										{t('registration-form.user-agreement')}
									</a>
								</div>
							</div>
						</Space>
						<Button
							loading={isLoading}
							className={styles.button}
							size="large"
							type="primary"
							htmlType="submit"
							disabled={!agreed || !formValid}
						>
							{registrationButtonText
								? t(registrationButtonText)
								: t('registration-form.register-a-company')}
						</Button>
					</Form>
				</>
			)}
		</div>
	);
};
